import { v4 as uuidv4 } from 'uuid';
import API from '../Config/Api';
import { getVisitorKey } from './visitorKey';
import { adOfLocation, currentAdVisit, isVisitOfAd, rememberAdVisit } from './adVisit';

/**
 * When a visitor arrives from one of our ads, the final URL carries `?stylon_cid=<campaignId>`
 * (added at publish) plus the platform's own click id (`gclid` from Google, `fbclid` from
 * Meta). Record that landing once per session so conversions can be attributed to the ad —
 * and later uploaded back to the platform for smart bidding. Fire-and-forget; never throws.
 *
 * Called for every storefront page that loads its business (see api/storeApi getSalonBySlug),
 * so a landing straight onto a product page from a catalogue ad is recorded too.
 *
 * A landing may name the exact ad and no campaign: a product tapped in a Meta ad's row of
 * products opens the catalogue item's link with the ad's tags appended, and ads published before
 * 2026-09-17 put only `stylon_ad` in those tags (seen live that night). It is recorded with the ad
 * alone, and the server credits it to that ad's campaign — among this business's campaigns only.
 *
 * It posts the click itself. It used to hand the click to a helper that posted five named
 * fields — and dropped the exact-ad id on the way, so no variant was ever credited.
 *
 * THE CLICK IS REMEMBERED (utils/adVisit.js): what the shopper does next in the shop is sent as
 * steps naming this click (utils/shopJourney.js), for this visit and for a return within seven
 * days. `slug` is the business's, so those steps can tell its pages from another business's.
 */
export function maybeRecordAdLanding(salonId, slug = null) {
  if (!salonId || typeof window === 'undefined') return;
  try {
    // Our campaign, and the exact ad filled in by the platform (`stylon_ad={{ad.id}}` on the
    // creative). A platform ad id is digits; an unfilled template tag or anything else is not sent.
    const ad = adOfLocation(window.location.search);
    if (!ad) return;
    const { campaignId, platformAdRef, platformClickId } = ad;
    // De-dupe: one recorded click per campaign — and per ad — per session (avoids re-firing on SPA
    // re-renders). A landing on the ad's own link names both, so a product tapped in its row later,
    // which names the ad alone, is the same click; so is the other way round.
    const dedupeKeys = [
      campaignId && `stylon_adclick_${campaignId}`,
      platformAdRef && `stylon_adclick_ad_${platformAdRef}`,
    ].filter(Boolean);
    if (dedupeKeys.some((key) => sessionStorage.getItem(key))) return;
    dedupeKeys.forEach((key) => sessionStorage.setItem(key, '1'));
    // The same platform click landing again in a new visit is the click already kept: the server
    // counts it once (same visitor, same platform click id, within a day) and writes no row for a
    // second id, so the steps keep naming the id it has.
    const kept = currentAdVisit();
    const clickId = kept && platformClickId && kept.platformClickId === platformClickId && isVisitOfAd(kept, ad)
      ? kept.clickId
      : uuidv4();
    rememberAdVisit({ clickId, campaignId, platformAdRef, platformClickId, salonId, slug });
    Promise.resolve(
      API.post('/ads/click', {
        salonId,
        ...(campaignId ? { campaignId } : {}),
        clickId,
        platformClickId,
        visitorKey: getVisitorKey() || undefined,
        ...(platformAdRef ? { platformAdRef } : {}),
      }),
    ).catch(() => {});
  } catch (_) {
    /* never throw from instrumentation */
  }
}
